import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  getWatchBrands,
  createBrandUser,
  type CreateBrandUserResult,
} from '../../services/firebase';
import type { BrandMembershipRole, WatchBrand } from '../../types';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { BrandFilterSelect } from '../CuratedWatches/BrandFilterSelect';

interface CreateBrandUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after a successful create so the caller can refresh the user list. */
  onCreated: () => void;
}

/**
 * Super-admin stopgap (Brand Portal Phase 1.2.5) for creating a brand user
 * without hopping between the Firestore and Auth consoles. Collects email,
 * brand, and role, then calls the `createBrandUser` callable.
 *
 * When the callable creates a brand-new Auth user it returns a one-time
 * `tempPassword`; we surface a dedicated credentials view (with a copy button
 * and a "won't be shown again" warning) since the admin shares it out of band.
 */
export function CreateBrandUserModal({
  isOpen,
  onClose,
  onCreated,
}: CreateBrandUserModalProps) {
  const [brands, setBrands] = useState<WatchBrand[]>([]);
  const [loadingBrands, setLoadingBrands] = useState(true);

  const [email, setEmail] = useState('');
  const [brandId, setBrandId] = useState('');
  const [role, setRole] = useState<BrandMembershipRole>('brandAdmin');
  const [submitting, setSubmitting] = useState(false);

  // Set once the callable returns a brand-new user — drives the credentials view.
  const [credentials, setCredentials] = useState<CreateBrandUserResult | null>(
    null,
  );

  // Load brands when the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoadingBrands(true);
    getWatchBrands()
      .then((list) => {
        if (!cancelled) {
          setBrands([...list].sort((a, b) => a.name.localeCompare(b.name)));
        }
      })
      .catch((err) => {
        console.error('Failed to load brands', err);
        if (!cancelled) toast.error('Failed to load brands');
      })
      .finally(() => {
        if (!cancelled) setLoadingBrands(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  // Reset all form + result state whenever the modal is (re)opened.
  useEffect(() => {
    if (isOpen) {
      setEmail('');
      setBrandId('');
      setRole('brandAdmin');
      setSubmitting(false);
      setCredentials(null);
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      toast.error('Email is required');
      return;
    }
    if (!brandId) {
      toast.error('Please select a brand');
      return;
    }

    setSubmitting(true);
    try {
      const result = await createBrandUser({
        email: trimmedEmail,
        brandId,
        role,
      });

      onCreated();

      if (result.tempPassword) {
        // New user — show the one-time credentials view; keep the modal open.
        setCredentials(result);
        toast.success('User created');
      } else {
        toast.success(
          result.existed ? 'Membership added' : 'User created',
        );
        onClose();
      }
    } catch (err) {
      console.error('Failed to create brand user', err);
      toast.error(
        err instanceof Error ? err.message : 'Failed to create brand user',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyPassword = async () => {
    if (!credentials?.tempPassword) return;
    try {
      await navigator.clipboard.writeText(credentials.tempPassword);
      toast.success('Password copied');
    } catch {
      toast.error('Copy failed — select and copy manually');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={credentials ? 'User Created' : 'Create Brand User'}
      size="md"
    >
      {credentials ? (
        // ---- Credentials view (new user only) ----
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            A new account was created. Share these credentials with the brand
            owner securely (e.g. a password manager or encrypted message).
          </p>
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <div>
              <span className="block text-xs font-medium text-gray-500 uppercase">
                Email
              </span>
              <span className="text-sm font-mono text-gray-900 break-all">
                {credentials.email}
              </span>
            </div>
            <div>
              <span className="block text-xs font-medium text-gray-500 uppercase">
                Temporary Password
              </span>
              <div className="flex items-center gap-2">
                <span className="text-sm font-mono text-gray-900 break-all">
                  {credentials.tempPassword}
                </span>
                <Button variant="secondary" size="sm" onClick={handleCopyPassword}>
                  Copy
                </Button>
              </div>
            </div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-sm text-amber-800">
              ⚠️ This password won't be shown again. Copy it now — if it's lost,
              the brand owner will need to reset their password.
            </p>
          </div>
          <div className="flex justify-end pt-2">
            <Button variant="primary" onClick={onClose}>
              Done
            </Button>
          </div>
        </div>
      ) : (
        // ---- Form view ----
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="owner@brand.com"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </div>

          <BrandFilterSelect
            brands={brands}
            loading={loadingBrands}
            value={brandId}
            onChange={setBrandId}
            label="Brand"
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role
            </label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as BrandMembershipRole)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            >
              <option value="brandAdmin">Brand Admin</option>
              <option value="brandMember">Brand Member</option>
            </select>
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t">
            <Button variant="secondary" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} loading={submitting}>
              Create User
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
